/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useState, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { object, string, arrayOf, PropTypes, bool, number } from 'prop-types';

// MUI
import {
    Grid,
    Button,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';

// Components
import MobileRecipientBookList from './MobileRecipientBookList';
import SortRecipientBook from '../Common/SortRecipientBook/SortRecipientBook';
import FilterRecipientBookModal from '../Common/FilterRecipientBook/FilterRecipientBookModal';
import SearchFilter from '../Common/FilterRecipientBook/SearchFilter';
import ErrorPage from '../../../../common/ErrorPage';
import AddNewRecipient from '../Common/AddNewRecipient/AddNewRecipient';
import AccountHome from '../../../MyAccount/AccountHome';
import RecipientBookBanner from '../Common/RecipientBookBanner';

// Styles
import { RecipientBookStyle } from '../Common/Assets/Style/RecipientBookStyle';

// Helpers
import { isEmpty } from '../../../../../../pages/Account/utils/object';
import { combineStyle } from '../../../../style/combineStyle';
import configENV from '../../../../config';
import { deepObjectExtend } from '../../../../utils/object';
import { getFeatureFlag } from '../../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { RecipientBookContext } from '../Common/Context/RecipientBookContext';
import { trackEvent } from '../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import PageTitle from '../../../../common/PageTitle';
import { maskPhoneNumber } from '../Common/Helpers/Helpers';
import { getAppRoutePrefix } from '../../../../../../helpers/common/helper';
// import StyledButton from '../../../../../../components/EnterpriseDesignSystem/Button/StyledButton';

const styles = ({ typography, palette }) => deepObjectExtend(
    combineStyle(typography, palette, {
        RecipientBookStyle,
    }),
);

const { RECIPIENTS_PER_PAGE } = configENV;
const MobileRecipientBook = ({ recipients, recipientBookListResError, classes }) => {
    const { recipientState, setRecipientState, filteredRecipients, setFilteredRecipients, setRecipientsCopy } = useContext(RecipientBookContext);
    const [isViewingAddNewRecipientForm, setIsViewingAddNewRecipientForm] = useState(false);
    const [visibleRecipientsCount, setVisibleRecipientsCount] = useState(RECIPIENTS_PER_PAGE);
    const isFilterEnabled = useSelector(getFeatureFlag('is-recipient-book-filter-on'));
    const isSortingEnabled = useSelector(getFeatureFlag('is-recipient-book-sorting-on'));
    const isRecipientBookBannerOn = useSelector(getFeatureFlag('is-recipient-book-banner-on'));
    const isLazyLoadingEnabled = useSelector(getFeatureFlag('is-account-contacts-lazy-loading-enabled'));

    const dispatch = useDispatch();

    const totalContacts = !isEmpty(filteredRecipients) ? filteredRecipients.addressBookResponse?.addressList?.length : recipientState?.addressBookResponse?.addressList?.length || 0;
    const contactsLabel = `${totalContacts} contact${totalContacts !== 1 ? 's' : ''}`;
    const selfContactCardCount = (filteredRecipients?.addressBookResponse ? filteredRecipients : recipientState)?.addressBookResponse?.addressList?.filter((address) => +address.relationship === 0)?.length;

    useEffect(() => {
        if (recipients?.addressBookResponse?.addressList?.length >= 0) {
            const updatedData = { ...recipients };
            updatedData.addressBookResponse.addressList = updatedData?.addressBookResponse?.addressList?.map((recipient) => {
                if (recipient.occasions?.length) {
                    recipient.occasions = recipient.occasions.map((occ) => {
                        if (occ.reminderPhone?.length) {
                            occ.reminderPhone = maskPhoneNumber(updatedData.addressBookResponse.reminderPhone || '');
                        } else {
                            occ['reminderPhone'] = maskPhoneNumber(updatedData.addressBookResponse.reminderPhone || '');
                        }
                        return occ;
                    })
                }
                return recipient;
            });
            setRecipientState(updatedData);
            setRecipientsCopy(updatedData);
        }
    }, [recipients]);

    useEffect(() => {
        if (recipientBookListResError === 'NO_RECIPIENTS_FOUND') {
            setRecipientState({
                addressBookResponse: {
                    addressList: []
                }
            });
        }
    }, [recipientBookListResError]);

    const lazyLoadContacts = () => setVisibleRecipientsCount(prevVisibleRecipientsCount => prevVisibleRecipientsCount + RECIPIENTS_PER_PAGE);
    const resetVisibleRecipientsCount = () => (visibleRecipientsCount !== RECIPIENTS_PER_PAGE && setVisibleRecipientsCount(RECIPIENTS_PER_PAGE));

    if (recipientBookListResError === 'GENERIC_API_ERROR') {
        return (
            <div className={classes.container}>
                <div className={classes.title}>Contacts &amp; Reminders</div>
                <ErrorPage />
            </div>
        );
    }


    // Show Load More button:
    // case 1: Filtered Recipients result from search box have more than 10 contacts to view more
    // case 2: Filtered Recipients result from search box OR recient state has more than 10 contacts
    // case 3: Filtered recipients are empty (empty search box) AND there are more than 10 contacts

    const filteredContactsLength = filteredRecipients?.addressBookResponse?.addressList?.length ?? 0;
    const contactListLength = recipientState?.addressBookResponse?.addressList?.length ?? 0;
    const hasFilteredResponse = !!filteredRecipients?.addressBookResponse;
    
    const isShowingLoadMoreBtn =
        (filteredContactsLength > 10 && visibleRecipientsCount <= filteredContactsLength) ||
        (filteredContactsLength > 10 && contactListLength > 10 && visibleRecipientsCount <= contactListLength) ||
        (!hasFilteredResponse && contactListLength > 10 && visibleRecipientsCount <= contactListLength);

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <AccountHome disablePageView />
            </div>
            <div style={{ margin: '24px 0px' }}>
                <PageTitle title="Contacts & Reminders" />
            </div>
            <div className={isRecipientBookBannerOn ? `${classes.searchField} ${classes.noMargin}` : classes.searchField}>
                <SearchFilter recipientState={recipientState} setFilteredRecipients={setFilteredRecipients} resetVisibleRecipientsCount={resetVisibleRecipientsCount} />
            </div>
            {isRecipientBookBannerOn && <RecipientBookBanner queryTitle="Address Book Banner" />}
            <Button
                fullWidth
                color="primary"
                variant="contained"
                className={`${classes.addNewContactBtn} ${classes.primaryBtn}`}
                onClick={() => {
                    dispatch(trackEvent({
                        eventName: "address_book_interaction",
                        interaction_type: "add_contact_start",
                        eventCategory: "Address Book",
                        eventAction: "Interaction",
                        eventLabel: "Add Contact Start"
                    }))
                    setIsViewingAddNewRecipientForm(true);
                    window.history.pushState('', '', getAppRoutePrefix() + '/account/address-book/add-new-recipient');
                }}
            >
                Add New Contact
            </Button>
            {/* <StyledButton
                fullWidth
                variant="primary"
                label='Add New Contact'
                handleOnClick={() => {
                    dispatch(trackEvent({
                        eventName: "address_book_interaction",
                        interaction_type: "add_contact_start",
                        eventCategory: "Address Book",
                        eventAction: "Interaction",
                        eventLabel: "Add Contact Start"
                    }))
                    setIsViewingAddNewRecipientForm(true);
                    window.history.pushState('', '', '/account/address-book/add-new-recipient');
                }}
            /> */}
            {isViewingAddNewRecipientForm && <AddNewRecipient classes={classes} recipientState={recipientState} setRecipientState={setRecipientState} setIsViewingAddNewRecipientForm={setIsViewingAddNewRecipientForm} />}
            <Grid className={classes.filterAndSortContainer}>
                {isFilterEnabled && <FilterRecipientBookModal recipientState={recipientState} classes={classes} />}
                {isSortingEnabled && <SortRecipientBook recipientState={recipientState} setRecipientState={setRecipientState} classes={classes} />}
            </Grid>
            <Grid container id='mobileRecipientBookContacts' className={classes.listHeading}>
                {
                    selfContactCardCount
                        ? (
                            <Grid item>
                                <p>My Contact Card{selfContactCardCount > 1 ? 's' : ''}</p>
                            </Grid>
                        ) : ''
                }
                <Grid item style={{ marginLeft: 'auto' }}>
                    <p className={classes.totalContacts}>{contactsLabel}</p>
                </Grid>
            </Grid>
            <MobileRecipientBookList
                classes={classes}
                lazyLoadContacts={isLazyLoadingEnabled ? lazyLoadContacts : null}
                recipientState={
                    filteredRecipients?.addressBookResponse
                    ? filteredRecipients
                    : recipientState
                }
                visibleRecipientsCount={visibleRecipientsCount}
            />
            <Grid container justifyContent='center' style={{ marginTop: '48px', marginBottom: '80px' }}>
                {
                    isShowingLoadMoreBtn && !isLazyLoadingEnabled && (
                        <Button
                            fullWidth
                            className={`${classes.outlinedPrimaryBtn} text-transform-none`}
                            onClick={() => {
                                setVisibleRecipientsCount(prevVisibleRecipientsCount => prevVisibleRecipientsCount + RECIPIENTS_PER_PAGE);
                            }}>Load More</Button>
                    )
                }
            </Grid>
        </>
    );
};

MobileRecipientBook.propTypes = {
    recipients: PropTypes.shape({
        addressBookResponse: PropTypes.shape({
            addressList: arrayOf(PropTypes.shape({
                addresses: arrayOf(PropTypes.shape({
                    addressBookEntryId: string,
                    addressLineOne: string,
                    addressType: string,
                    city: string,
                    country: string,
                    firstName: string,
                    isBillingAddress: bool,
                    lastName: string,
                    lastUpdateDate: string,
                    phoneNumber: string,
                    rcpId: string,
                    state: string,
                    zip: string,
                })),
                occasions: arrayOf(PropTypes.shape({
                    desc: string,
                    rcpId: string,
                    dateDD: string,
                    dateMM: string,
                    dateYYYY: string,
                    subscribed: bool,
                    occasion: string,
                    occasionId: string,
                    createdDate: string,
                    createdUser: string,
                    reminderEmail: string,
                    reminderPhone: string,
                    subscribedEmail: bool,
                    subscribedPhone: bool,
                })),
                rcpAddressBookEntryId: string,
                rcpFirstName: string,
                rcpId: string,
                rcpLastName: string,
            })),
            contactId: string,
            reminderEmail: string,
            totalEntries: number,
        }),
    }),
    recipientBookListResError: string.isRequired,
    classes: object.isRequired,
};

export default withStyles(styles)(MobileRecipientBook);
